import styled from "styled-components";

export const WorkspacePage = styled.div`
  min-height: 100%;
  padding-bottom: 42px;
`;

export const WorkspaceHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 28px clamp(18px, 4vw, 40px) 20px;
  @media (max-width: 700px) { flex-direction: column; }
`;

export const WorkspaceTitle = styled.div`
  min-width: 0;
  h1 { margin: 0; color: #0f172a; font-size: clamp(24px, 4vw, 34px); letter-spacing: -0.06em; }
  p { margin-top: 8px; color: #64748b; font-size: 13px; line-height: 1.6; }
`;

export const WorkspaceActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

export const WorkspaceBody = styled.div`
  display: grid;
  gap: 16px;
  padding: 0 clamp(18px, 4vw, 40px);
`;

export const WorkspaceToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 9px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
`;

export const WorkspaceInput = styled.input`
  min-height: 38px;
  min-width: 180px;
  padding: 0 11px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  color: #0f172a;
  background: #fff;
  font-size: 13px;
  outline: none;
  &:focus { border-color: #a5b4fc; box-shadow: 0 0 0 4px rgba(99,102,241,.1); }
`;

export const WorkspaceTextarea = styled.textarea`
  min-height: 92px;
  width: 100%;
  resize: vertical;
  padding: 10px 11px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  color: #0f172a;
  background: #fff;
  font-size: 13px;
  line-height: 1.5;
  outline: none;
  &:focus { border-color: #a5b4fc; box-shadow: 0 0 0 4px rgba(99,102,241,.1); }
`;

export const WorkspaceSelect = styled.select`
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  color: #334155;
  background: #fff;
  font-size: 13px;
`;

export const WorkspaceButton = styled.button<{ $primary?: boolean; $danger?: boolean; $quiet?: boolean }>`
  min-height: 38px;
  padding: 0 13px;
  border: 1px solid ${({ $primary, $danger }) => ($primary ? "#4f46e5" : $danger ? "#fecaca" : "#e2e8f0")};
  border-radius: 9px;
  color: ${({ $primary, $danger }) => ($primary ? "#fff" : $danger ? "#b91c1c" : "#475569")};
  background: ${({ $primary, $danger, $quiet }) => ($primary ? "#4f46e5" : $danger ? "#fff1f2" : $quiet ? "transparent" : "#fff")};
  font-size: 12px;
  font-weight: 800;
  &:hover, &:focus-visible { border-color: ${({ $primary }) => ($primary ? "#4338ca" : "#a5b4fc")}; color: ${({ $primary }) => ($primary ? "#fff" : "#4338ca")}; }
  &:disabled { cursor: not-allowed; opacity: .5; }
`;

export const WorkspaceCard = styled.section`
  min-width: 0;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  background: #fff;
  box-shadow: 0 8px 25px rgba(15,23,42,.035);
  h2 { margin: 0 0 10px; color: #0f172a; font-size: 17px; letter-spacing: -0.035em; }
  h3 { margin: 0 0 8px; color: #0f172a; font-size: 14px; }
`;

export const WorkspaceGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 1000px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

export const WorkspaceTable = styled.div`
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  table { width: 100%; border-collapse: collapse; min-width: 680px; }
  th, td { padding: 11px 12px; border-bottom: 1px solid #f1f5f9; text-align: left; vertical-align: top; font-size: 12px; }
  th { color: #64748b; background: #f8fafc; font-size: 11px; font-weight: 800; white-space: nowrap; }
  tr:last-child td { border-bottom: 0; }
`;

export const WorkspaceTabs = styled.nav`
  display: flex;
  gap: 5px;
  overflow-x: auto;
  padding: 0 clamp(18px, 4vw, 40px);
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
`;

export const WorkspaceTab = styled.button<{ $active: boolean }>`
  min-height: 42px;
  padding: 0 11px;
  border-bottom: 2px solid ${({ $active }) => ($active ? "#4f46e5" : "transparent")};
  color: ${({ $active }) => ($active ? "#4338ca" : "#64748b")};
  background: transparent;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
`;

export const WorkspaceForm = styled.form`
  display: grid;
  gap: 12px;
`;

export const WorkspaceField = styled.label`
  display: grid;
  gap: 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
  small { color: #94a3b8; font-size: 11px; font-weight: 600; }
`;

export const ArrayEditor = styled.div`
  display: grid;
  gap: 7px;
`;

export const ArrayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  & > input { flex: 1; }
`;

export const StatusPill = styled.span<{ $tone?: "success" | "warning" | "danger" | "neutral" }>`
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  color: ${({ $tone = "neutral" }) => ({ success: "#166534", warning: "#92400e", danger: "#b91c1c", neutral: "#475569" }[$tone])};
  background: ${({ $tone = "neutral" }) => ({ success: "#dcfce7", warning: "#fef3c7", danger: "#fee2e2", neutral: "#f1f5f9" }[$tone])};
  font-size: 10px;
  font-weight: 900;
  white-space: nowrap;
`;

export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(15,23,42,.48);
`;

export const ModalCard = styled.section`
  width: min(100%, 520px);
  max-height: min(90vh, 720px);
  overflow-y: auto;
  padding: 22px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 30px 90px rgba(15,23,42,.25);
  h2 { margin-bottom: 8px; color: #0f172a; font-size: 19px; }
  p { color: #64748b; font-size: 12px; line-height: 1.6; }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 15px;
`;

export const InlineNotice = styled.p<{ $danger?: boolean }>`
  margin: 0;
  color: ${({ $danger }) => ($danger ? "#b91c1c" : "#64748b")};
  font-size: 12px;
  line-height: 1.6;
`;

export const ConditionList = styled.ul`
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  li { display: flex; gap: 8px; align-items: flex-start; padding: 9px 10px; border-radius: 9px; background: #f8fafc; color: #475569; font-size: 12px; line-height: 1.5; }
  li::before { content: "•"; color: #4f46e5; font-weight: 900; }
`;
